/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import DashboardChart from 'components/Chart';
import { getQuantityStatistics } from 'apis/dashboard.api';
import { getProjectTypes } from 'utils/func';
import Loading from 'components/Loading';
import ProjectVerticalBarFilter from './ProjectVerticalBarFilter';

export default function ProjectVerticalBar({ organizations, programs }) {
  const { t } = useTranslation();
  const [isLoadedFilterFromLocalStorage, setIsLoadedFilterFromLocalStorage] = useState(false);

  const [filters, setFilters] = useState({
    programId: [],
    projectType: [],
    organizationId: [],
  });
  const [appliedFilters, setAppliedFilters] = useState({
    programId: [],
    projectType: [],
    organizationId: [],
  });

  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const projectTypes = getProjectTypes(t);

  const hasActiveFilters =
    appliedFilters.programId?.length > 0 || appliedFilters.projectType?.length > 0;

  const { data: quantityStatisticsData, isLoading } = useQuery(
    ['quantityStatistics', appliedFilters],
    () => getQuantityStatistics(appliedFilters),
    {
      refetchOnWindowFocus: false,
      enabled: isLoadedFilterFromLocalStorage,
    }
  );

  const data = quantityStatisticsData?.data || [];

  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});

  useEffect(() => {
    if (data && Array.isArray(data)) {
      const documentStyle = getComputedStyle(document.documentElement);
      const textColor = documentStyle.getPropertyValue('--text-color');
      const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
      const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

      const labels = data.map((item) => item.year);
      const submittedData = data.map((item) => item.submitted);
      const approvedData = data.map((item) => item.approved);

      setChartData({
        labels,
        datasets: [
          {
            label: t('dashboard.quantityProject.label.submitted'),
            backgroundColor: documentStyle.getPropertyValue('--blue-500'),
            borderColor: documentStyle.getPropertyValue('--blue-500'),
            data: submittedData,
          },
          {
            label: t('dashboard.quantityProject.label.approved'),
            backgroundColor: documentStyle.getPropertyValue('--green-500'),
            borderColor: documentStyle.getPropertyValue('--green-500'),
            data: approvedData,
          },
        ],
      });

      setChartOptions({
        maintainAspectRatio: false,
        aspectRatio: 0.8,
        plugins: {
          legend: {
            labels: {
              color: textColor,
              generateLabels(chart) {
                const { data: _data } = chart;
                return _data.datasets.map((dts, i) => {
                  const value = dts.data.reduce((sum, val) => sum + val, 0);
                  return {
                    text: `${dts.label} (${value})`,
                    fillStyle: dts.backgroundColor,
                    strokeStyle: dts.borderColor || dts.backgroundColor,
                    lineWidth: 1,
                    datasetIndex: i,
                    hidden: !chart.isDatasetVisible(i),
                  };
                });
              },
            },
            position: 'bottom',
          },
          datalabels: {
            color: textColorSecondary,
            font: {
              weight: 'bold',
              size: 11,
            },
            anchor: 'end',
            align: 'top',
          },
        },
        scales: {
          x: {
            ticks: {
              color: textColorSecondary,
              font: {
                weight: 500,
              },
            },
            grid: {
              display: false,
              drawBorder: false,
            },
          },
          y: {
            ticks: {
              color: textColorSecondary,
            },
            grid: {
              color: surfaceBorder,
              drawBorder: false,
            },
          },
        },
      });
    }
  }, [data, t]);

  const formatShowAppliedFilters = () => {
    let text = '';
    if (appliedFilters.programId?.length > 0) {
      text += `${t('dashboard.projectRevise.dropdown.program')}: ${appliedFilters.programId
        .map((id) => programs.find((prog) => prog.id === id)?.name)
        .join(', ')}; `;
    }

    if (appliedFilters.projectType?.length > 0) {
      text += `${t('dashboard.projectRevise.dropdown.projectType')}: ${appliedFilters.projectType
        .map((type) => projectTypes.find((pt) => pt.value === type)?.name)
        .join(', ')}; `;
    }

    if (appliedFilters.organizationId?.length > 0) {
      text += `${t(
        'dashboard.projectRevise.dropdown.organization'
      )}: ${appliedFilters.organizationId
        .map((id) => organizations.find((org) => org.id === id)?.name)
        .join(', ')}; `;
    }

    text = text.trim().replace(/;$/, '');
    return <div className="text-wrap">{text}</div>;
  };

  useEffect(() => {
    setIsLoadedFilterFromLocalStorage(true);
    const savedFilters = localStorage.getItem('admin-dashboard-projects-vertical-filters');
    if (!savedFilters) {
      return;
    }

    const parsed = JSON.parse(savedFilters);
    setFilters(parsed);
    setAppliedFilters(parsed);
  }, []);

  if (isLoading) return <Loading />;

  return (
    <div className="card p-3 md:p-5">
      <div className="card-header border-bottom-1 surface-border pb-3 mb-4">
        <div className="flex justify-content-between align-items-center">
          <h3 className="text-2xl text-900 font-semibold m-0">
            {t('dashboard.quantityProject.title')}
          </h3>
          <Button
            icon="pi pi-filter"
            label={t('dashboard.filter.label.button')}
            severity={hasActiveFilters ? 'success' : 'secondary'}
            outlined={!hasActiveFilters}
            onClick={() => setShowFilterDialog(true)}
            className="p-button-sm"
          />
        </div>
      </div>

      <ProjectVerticalBarFilter
        showFilterDialog={showFilterDialog}
        onHide={() => setShowFilterDialog(false)}
        filters={filters}
        setFilters={setFilters}
        setAppliedFilters={setAppliedFilters}
        programs={programs}
        projectTypes={projectTypes}
        organizations={organizations}
        localStorageKey="admin-dashboard-projects-vertical-filters"
      />

      <div className="flex flex-column align-items-center px-4 pb-4 gap-2">
        {formatShowAppliedFilters()}
        <DashboardChart
          type="bar"
          data={chartData}
          options={chartOptions}
          className="w-full"
          style={{ height: '450px', cursor: 'pointer' }}
        />
      </div>
    </div>
  );
}

ProjectVerticalBar.propTypes = {
  organizations: PropTypes.array.isRequired,
  programs: PropTypes.array.isRequired,
};
